import {
  isAreaFeature,
  type AreaFeature,
  type Coordinate,
  type EditableFloorPlan,
  type EditorFeature,
  type MultiPolygonCoordinates,
} from '../models/editor-feature';
import { toScreen, type Projection } from './projection';
import { editableFeaturesOf } from './selection';

/**
 * Which feature is under the pointer, and which features a marquee caught.
 *
 * On the SVG renderer this came free: each feature was an element, and the browser decided
 * what a click landed on. A canvas has no elements, so the question has to be answered
 * explicitly — and answered in the same order the painting happens, or the topmost thing on
 * screen is not the thing that gets picked.
 *
 * Everything here works in surface coordinates rather than plan coordinates. A desk is drawn
 * at a fixed radius on the surface however far out the plan is zoomed, so its clickable area
 * has to be measured there too; testing in plan units would make a desk on a large site
 * unclickably small and one on a small site catch half the floor.
 */

/** How far from a shape's edge still counts as hitting it, in surface units. */
export const PICK_TOLERANCE = 4;

/** Matches the radius `draw-plan` gives a desk marker, plus room for a fingertip. */
const DESK_PICK_RADIUS = 8;

/** A generous box around an annotation's anchor: text metrics are not available off-canvas. */
const LABEL_PICK_RADIUS = 12;

/**
 * The selectable features in the order they are painted.
 *
 * Searched backwards, so the topmost thing on screen is what a click lands on: a desk marker
 * over its own desk drawing belongs to the marker, and a desk over a wall belongs to the
 * desk — the answer the browser used to give for free. `editableFeaturesOf` is kept in this
 * same order for exactly that reason, so the two cannot drift apart.
 */
const inPaintingOrder = (floorPlan: EditableFloorPlan): EditorFeature[] => editableFeaturesOf(floorPlan);

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Rectangle {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

const distanceTo = (point: Point, target: Point): number => Math.hypot(point.x - target.x, point.y - target.y);

/** Shortest distance from a point to a line segment, which is what "near an edge" means. */
export const distanceToSegment = (point: Point, from: Point, to: Point): number => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return distanceTo(point, from);
  }

  const t = Math.max(0, Math.min(1, ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared));

  return distanceTo(point, { x: from.x + t * dx, y: from.y + t * dy });
};

/** Ray casting, per ring. A point inside an odd number of rings is inside the shape. */
const isInsideRing = (point: Point, ring: readonly Point[]): boolean => {
  let inside = false;

  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
    const a = ring[index];
    const b = ring[previous];

    if ((a.y > point.y) !== (b.y > point.y)
      && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }

  return inside;
};

interface WorldBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

/**
 * The box round an area, in plan coordinates, worked out once for each outline. An outline is
 * replaced rather than changed whenever it is edited, so one worked out is never stale.
 */
const worldBounds = new WeakMap<MultiPolygonCoordinates, WorldBounds>();
const worldBoundsOf = (coordinates: MultiPolygonCoordinates): WorldBounds => {
  const known = worldBounds.get(coordinates);
  if (known) return known;

  let [minX, minY, maxX, maxY] = [Infinity, Infinity, -Infinity, -Infinity];
  coordinates.forEach((polygon) => polygon.forEach((ring) => ring.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  })));

  const bounds = {
    minX, minY, maxX, maxY,
  };
  worldBounds.set(coordinates, bounds);
  return bounds;
};

const projectRings = (
  coordinates: MultiPolygonCoordinates,
  projection: Projection,
): Point[][] => coordinates.flatMap(
  (polygon) => polygon.map((ring) => ring.map((vertex) => toScreen(vertex, projection))),
);

/**
 * Whether a plan point lies inside an area, in the plan's own coordinates.
 *
 * No projection and no tolerance: this answers "is it under it", not "did the click land on
 * it". Odd-winding, like the on-screen test, so a point in a hole is outside.
 */
export const containsPoint = (coordinates: MultiPolygonCoordinates, at: Coordinate): boolean => {
  const point = { x: at[0], y: at[1] };
  const rings = coordinates.flatMap((polygon) => polygon.map((ring) => ring.map(([x, y]) => ({ x, y }))));

  return rings.filter((ring) => isInsideRing(point, ring)).length % 2 === 1;
};

/**
 * Whether a point is on a drawn area — inside it, or close enough to its edge.
 *
 * The edge test is not decoration. A wall drawn as a closed ring a few units thick is
 * hairline-thin on screen at a fitted zoom, and requiring a click strictly inside it would
 * make most of a plan's structure unselectable.
 */
export const isOnArea = (
  point: Point,
  coordinates: MultiPolygonCoordinates,
  projection: Projection,
  tolerance = PICK_TOLERANCE,
): boolean => {
  // Off the box round it is off it: most areas are, and a traced plan has thousands of points
  // to project for each that is not.
  const bounds = worldBoundsOf(coordinates);
  if (!Number.isFinite(bounds.minX)) return false;
  const topLeft = toScreen([bounds.minX, bounds.maxY], projection);
  const bottomRight = toScreen([bounds.maxX, bounds.minY], projection);
  if (point.x < Math.min(topLeft.x, bottomRight.x) - tolerance || point.x > Math.max(topLeft.x, bottomRight.x) + tolerance
    || point.y < Math.min(topLeft.y, bottomRight.y) - tolerance || point.y > Math.max(topLeft.y, bottomRight.y) + tolerance) {
    return false;
  }

  const rings = projectRings(coordinates, projection);

  // Odd-winding, so a point in a hole is outside the shape — which is what a hole is.
  const crossings = rings.filter((ring) => isInsideRing(point, ring)).length;

  if (crossings % 2 === 1) {
    return true;
  }

  return rings.some((ring) => ring.some((vertex, index) => (
    index > 0 && distanceToSegment(point, ring[index - 1], vertex) <= tolerance
  )));
};

const isOnFeature = (point: Point, feature: EditorFeature, projection: Projection): boolean => {
  switch (feature.kind) {
    case 'desk':
      // Grown with the icon, so an enlarged desk can be picked anywhere it is drawn.
      return distanceTo(point, toScreen(feature.coordinates, projection)) <= DESK_PICK_RADIUS * (feature.iconScale ?? 1);
    case 'textLabel':
    case 'iconLabel':
      return distanceTo(point, toScreen(feature.coordinates, projection)) <= LABEL_PICK_RADIUS;
    default:
      return isOnArea(point, feature.coordinates, projection);
  }
};

/** How much of the screen an area covers, as the area of its bounding box in square pixels. */
const screenFootprintOf = (feature: EditorFeature, projection: Projection): number => {
  if (!isAreaFeature(feature)) {
    return 0;
  }

  // The box round it, scaled: the projection only scales and moves, so this is the projected box.
  const bounds = worldBoundsOf(feature.coordinates);

  if (!Number.isFinite(bounds.minX)) {
    return 0;
  }

  return ((bounds.maxX - bounds.minX) * projection.scale) * ((bounds.maxY - bounds.minY) * projection.scale);
};

export interface PickOptions {
  /** Editor ids of features that are not drawn, and so cannot be under the pointer. */
  readonly hidden?: ReadonlySet<string>;
  /**
   * Which features a click may land on at all. Everything, when absent.
   *
   * What an editing mode narrows: with "Edit rooms" armed, a click inside a room that also
   * has a desk and a wall in it means the room, and nothing else on the plan should answer.
   */
  readonly accept?: (feature: EditorFeature) => boolean;
}

const isPickable = (feature: EditorFeature, options: PickOptions): boolean => (
  !options.hidden?.has(feature.id) && (options.accept?.(feature) ?? true)
);

/**
 * The feature under the pointer, or undefined.
 *
 * Point features first, in reverse painting order: a desk marker sitting on its own desk
 * drawing belongs to the marker, not to the drawing underneath it.
 *
 * Areas are then resolved by **how much of the screen they cover, smallest first** — not by
 * painting order, which is what this used to do and which got it exactly backwards. Every room
 * on a floorplan sits inside the building's outline, and that outline is drawn last of the
 * areas, so a click anywhere inside the building matched the border first: clicking a room
 * selected a rectangle spanning the whole plan, and the room itself was unreachable. The same
 * went for a desk outline inside a room.
 *
 * Smallest-first is what every drawing tool does, and it is the only rule that makes a
 * containment hierarchy clickable: the most specific thing under the cursor is the thing the
 * admin is pointing at.
 */
export const pickFeature = (
  point: Point,
  floorPlan: EditableFloorPlan,
  projection: Projection,
  options: PickOptions = {},
): EditorFeature | undefined => {
  const visible = inPaintingOrder(floorPlan).filter((feature) => isPickable(feature, options));

  for (let index = visible.length - 1; index >= 0; index -= 1) {
    const feature = visible[index];

    if (!isAreaFeature(feature) && isOnFeature(point, feature, projection)) {
      return feature;
    }
  }

  const areas = visible.filter((feature) => isAreaFeature(feature) && isOnFeature(point, feature, projection));

  if (!areas.length) {
    return undefined;
  }

  // `<=`, so two areas of the same size are settled by painting order — the later one is the
  // one drawn on top, and that is the one the cursor is pointing at. A plan whose floor plate
  // and building outline are the same rectangle is exactly this case.
  return areas.reduce((smallest, feature) => (
    screenFootprintOf(feature, projection) <= screenFootprintOf(smallest, projection) ? feature : smallest
  ));
};

const anchorOf = (feature: EditorFeature, projection: Projection): Point[] => {
  if (feature.kind === 'desk' || feature.kind === 'textLabel' || feature.kind === 'iconLabel') {
    return [toScreen(feature.coordinates, projection)];
  }

  return projectRings(feature.coordinates, projection).flat();
};

const contains = (rectangle: Rectangle, point: Point): boolean => (
  point.x >= rectangle.x
  && point.x <= rectangle.x + rectangle.width
  && point.y >= rectangle.y
  && point.y <= rectangle.y + rectangle.height
);

/**
 * Everything a marquee caught.
 *
 * A point feature qualifies when its anchor is inside the box; an area qualifies when *any*
 * of its vertices is. Any rather than all, because dragging a box over part of a long wall
 * to select it is the gesture people actually make — requiring the whole shape would mean a
 * plan's largest features could only be caught by a marquee covering the entire plan.
 */
export const featuresIn = (
  rectangle: Rectangle,
  floorPlan: EditableFloorPlan,
  projection: Projection,
  options: PickOptions = {},
): EditorFeature[] => inPaintingOrder(floorPlan)
  .filter((feature) => isPickable(feature, options))
  .filter((feature) => anchorOf(feature, projection).some((point) => contains(rectangle, point)));

/**
 * The vertex of a drawn area nearest the pointer, when one is close enough to grab.
 *
 * Reported with the ring and position it sits at, because moving a vertex means rewriting
 * exactly that entry and nothing else.
 */
export interface VertexHit {
  readonly feature: EditorFeature;
  readonly polygonIndex: number;
  readonly ringIndex: number;
  readonly vertexIndex: number;
  readonly at: Coordinate;
}

export const pickVertex = (
  point: Point,
  features: readonly EditorFeature[],
  projection: Projection,
  tolerance = PICK_TOLERANCE + 2,
): VertexHit | undefined => {
  let best: VertexHit | undefined;
  let bestDistance = tolerance;

  features.forEach((feature) => {
    if (feature.kind === 'desk' || feature.kind === 'textLabel' || feature.kind === 'iconLabel') {
      return;
    }

    feature.coordinates.forEach((polygon, polygonIndex) => {
      polygon.forEach((ring, ringIndex) => {
        ring.forEach((vertex, vertexIndex) => {
          const distance = distanceTo(point, toScreen(vertex, projection));

          if (distance <= bestDistance) {
            bestDistance = distance;
            best = {
              feature, polygonIndex, ringIndex, vertexIndex, at: vertex,
            };
          }
        });
      });
    });
  });

  return best;
};

export interface EdgeHit {
  readonly feature: AreaFeature;
  readonly polygonIndex: number;
  readonly ringIndex: number;
  /** The vertex the new point goes after, so the ring keeps its winding. */
  readonly afterVertex: number;
}

/**
 * The edge nearest a point, among the shapes given.
 *
 * What makes "click an edge to add a corner there" possible. Reported as the vertex to insert
 * *after* rather than as a position, because a ring is an ordered run and a point added in
 * the wrong place turns an outline into a bow tie.
 */
export const nearestEdge = (
  point: Point,
  features: readonly AreaFeature[],
  projection: Projection,
  tolerance = PICK_TOLERANCE + 3,
): EdgeHit | undefined => {
  let best: EdgeHit | undefined;
  let bestDistance = tolerance;

  features.forEach((feature) => {
    feature.coordinates.forEach((polygon, polygonIndex) => {
      polygon.forEach((ring, ringIndex) => {
        for (let index = 0; index < ring.length - 1; index += 1) {
          const from = toScreen(ring[index], projection);
          const to = toScreen(ring[index + 1], projection);
          const distance = distanceToSegment(point, from, to);

          if (distance <= bestDistance) {
            bestDistance = distance;
            best = { feature, polygonIndex, ringIndex, afterVertex: index };
          }
        }
      });
    });
  });

  return best;
};
